// Pure helpers for showing several member posts' moderation state as one:
// a distributed post's destinations, a Command Center family row's members.
// A member is { organization_id, organization_name, organization_slug,
// post_id, moderation } (destinations and group_members share the shape).
import { isModerationHeld, moderationHoldBadge } from './moderationHoldStatus'

// Worst first, by badge tone.
const TONE_RANK = { danger: 0, warning: 1, info: 2 }

function lowerFirst(text) {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text
}

// heldMembers lists the members moderation holds, rejected or whose check
// failed, each with its badge, worst first. A member that only carries
// moderation state without being held (a live post moderation never
// checked) is left out.
export function heldMembers(members = []) {
  return (members || [])
    .filter((member) => isModerationHeld(member?.moderation))
    .map((member) => ({ member, badge: moderationHoldBadge(member?.moderation) }))
    .filter((entry) => entry.badge)
    .sort((a, b) => (TONE_RANK[a.badge.tone] ?? 3) - (TONE_RANK[b.badge.tone] ?? 3))
}

function bookCount(members) {
  return new Set((members || []).map((member) => member.organization_id)).size
}

// membersModerationBadge is the single chip for a row covering members: the
// held member's own badge when the row covers one book, else "Held by
// moderation in N of M books" in the worst member's tone; null when nothing
// is held.
export function membersModerationBadge(members = []) {
  const held = heldMembers(members)
  if (!held.length) return null
  const books = bookCount(members)
  if (books <= 1) return held[0].badge
  const heldBooks = bookCount(held.map((entry) => entry.member))
  return { key: 'moderation', label: `Held by moderation in ${heldBooks} of ${books} books`, tone: held[0].badge.tone }
}

// postGroupHeldNotice is the group page's warning, or '' when nothing is held.
export function postGroupHeldNotice(destinations = []) {
  const held = heldMembers(destinations)
  if (!held.length) return ''
  const heldBooks = bookCount(held.map((entry) => entry.member))
  return `Moderation is holding this post in ${heldBooks} of ${bookCount(destinations)} books. Open Moderation… on a book below to see why.`
}

// postGroupSaveMessage is the toast after a distributed post is created or
// updated, from the mutation answer's destinations: { tone, message }.
export function postGroupSaveMessage(result, isEdit) {
  const lead = isEdit ? 'Distributed post updated' : 'Distributed post created'
  const destinations = result?.destinations || []
  const held = heldMembers(destinations)
  if (!held.length) return { tone: 'success', message: lead }
  const heldBooks = bookCount(held.map((entry) => entry.member))
  const list = held
    .map(({ member, badge }) => `${member.organization_name || member.organization_slug} (${lowerFirst(badge.label)})`)
    .join(', ')
  return { tone: 'warning', message: `${lead}. Not live yet in ${heldBooks} of ${bookCount(destinations)} books: ${list}.` }
}
